import React, { useState, useEffect } from 'react';
import { Button, Card } from 'react-bootstrap';
import { BsChevronLeft, BsChevronRight } from 'react-icons/bs';

import { format } from 'date-fns';
import ErrorBoundary from '../ErrorBoundary';
import ChartContainer from './ChartContainer';
import { parseDateOnly } from '../../utils/dateUtils';

const BalanceGraph = ({ data, onPrevWeek, onNextWeek, fortnightStartDate }) => {
  const [currentWeekStart, setCurrentWeekStart] = useState(0);
  const [currentWeekData, setCurrentWeekData] = useState([]);

  useEffect(() => {
    if (data && Array.isArray(data) && data.length > 0) {
      const baseWeekData = data.slice(currentWeekStart, Math.min(currentWeekStart + 7, data.length));
      setCurrentWeekData(baseWeekData);
    }
  }, [data, currentWeekStart]);


  if (!data || !Array.isArray(data) || data.length === 0) {
    return <div>No data available or invalid data format</div>;
  }

  if (currentWeekData.length === 0) {
    return <div>No data available for the current week</div>;
  }

  const handlePreviousWeek = () => {
    if (currentWeekStart > 0) {
      const prevStart = Math.max(0, currentWeekStart - 7);
      setCurrentWeekStart(prevStart);
      onPrevWeek && onPrevWeek();
    }
  };

  const handleNextWeek = () => {
    const nextStart = Math.min(data.length - 7, currentWeekStart + 7);
    setCurrentWeekStart(nextStart);
    onNextWeek && onNextWeek();
  };

  const isPrevDisabled = currentWeekStart === 0;
  const isNextDisabled = currentWeekStart >= data.length - 7;

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(value);
  };

  const weekSummary = currentWeekData.reduce((acc, day) => {
    acc.totalIncome += day.income;
    acc.totalExpenses += day.expenses;
    return acc;
  }, { totalIncome: 0, totalExpenses: 0 });

  const formatDateSafely = (date) => {
    try {
      const dt = parseDateOnly(date);
      return format(dt, 'dd MMM');
    } catch (error) {
      console.error('Error parsing date:', error.name);
      return 'Invalid Date';
    }
  };

  const formatDateRange = (date) => {
    const options = { weekday: 'short', day: 'numeric', month: 'short' };
    const dt = parseDateOnly(date);
    return dt.toLocaleDateString('en-US', options).replace(',', '');
  };

  const dateRange = currentWeekData.length >= 2
    ? `${formatDateRange(currentWeekData[0].date)} - ${formatDateRange(currentWeekData[currentWeekData.length - 1].date)}`
    : 'N/A';

  return (
    <Card className="w-full max-w-4xl mx-auto bg-gradient-to-br from-background to-secondary/10">
      <Card.Header>
        <Card.Title className="text-3xl font-bold text-center bg-clip-text text-transparent bg-gradient-to-r from-primary to-primary-foreground">7-day Cash Flow</Card.Title>
    <div style={{ textAlign: "center", marginBottom: "20px" }}>
      <Button
        variant="light"
        onClick={handlePreviousWeek}
        disabled={isPrevDisabled}
        style={{
          marginRight: '40px',
        }}
      >
        <BsChevronLeft style={{ margin: 'auto' }} />
      </Button>
        <span className="text-center text-muted-foreground mt-2 flex items-center justify-center">
          {dateRange}
        </span>
              <Button
        variant="light"
        onClick={handleNextWeek}
        disabled={isNextDisabled}
        style={{
          marginLeft: '40px',
        }}
      >
        <BsChevronRight style={{ margin: 'auto' }} />
      </Button>

      </div>
      </Card.Header>
      <Card.Body>
        <div className="h-[300px] w-full">
          <ErrorBoundary>
            <ChartContainer
              currentWeekData={currentWeekData}
              fortnightStartDate={fortnightStartDate}
              options={{ plugins: { legend: { display: false } } }}
            />
          </ErrorBoundary>
        </div>
      </Card.Body>
    </Card>
  );
};

export default BalanceGraph;